/*
 * 戒导航 · 上传模块（jiedh/upload）—— 方案 B 的「三腿」注册
 * =============================================================================
 * 设计依据：doc/输入框富文本编辑器设计方案.md §8.5（方案 B 机制）
 *
 * fof/rich-text 的富文本编辑器由三个可扩展的构件组成，三者都通过 Flarum 的公开扩展点
 * `extend()` 挂接（**不修改 vendor/**）：
 *
 *   腿 A · TiptapEditorDriver.buildExtensions()   —— 往编辑器扩展列表里加自研节点
 *   腿 B · MarkdownParserBuilder.buildTokenizer() —— 让 markdown-it 认得出 [upl-image-preview …]
 *          MarkdownParserBuilder.buildTokens()    —— 把这个 token 映射成上面的节点
 *   腿 C · MarkdownSerializerBuilder.buildNodes() —— 把节点**原样**还原回 BBCode
 *
 * 三者必须同批交付：
 *   · 只做 B 不做 C → 提交时序列化器遇到未注册的节点会抛错；
 *   · 只做 B 不做 A → parser 找不到名为 uplImagePreview 的节点，同样抛错。
 *
 * 【为什么用字符串路径而不是 import】
 *   `extend('ext:命名空间/模块路径', …)` 会被解析为 flarum.reg.onLoad(namespace, id, …)：
 *   模块若尚未加载则先挂起、等它注册时再执行，因此与扩展加载顺序无关，
 *   也无需把 fof/rich-text 的前端打进我们自己的 bundle。
 *   命名空间由 `fof/rich-text` 推导为 `fof-rich-text`（ExportRegistry::namespaceAndIdFromPath）。
 *   三个 id 均取自 fof/rich-text 构建产物里的 flarum.reg.add 语句，见
 *   TiptapEditorDriver.js 里的注册清单。
 *
 * 【关于 extend 回调的首行判空】
 *   Flarum 的 extend() 在目标方法不存在时会把 value 传成 undefined 仍照常调用回调，
 *   因此每个回调都必须先判空再使用，避免抛错影响编辑器整体构建。
 */
import { extend } from 'flarum/common/extend';
import { UPL_IMAGE_PREVIEW_NODE, UPL_IMAGE_PREVIEW_TOKEN, formatUplImagePreview, parseUplImagePreviewAttributes, toNodeAttrs, tokenizeUplImagePreview } from './bbcode';
import { createUplImagePreviewNode } from './UplImagePreview';

/** 腿 A 宿主：编辑器驱动类。 */
const TIPTAP_EDITOR_DRIVER = 'ext:fof/rich-text/common/tiptap/TiptapEditorDriver';

/** 腿 B 宿主：markdown-it 分词器与 token 表的构建器。 */
const MARKDOWN_PARSER_BUILDER = 'ext:fof/rich-text/common/tiptap/markdown/MarkdownParserBuilder';

/** 腿 C 宿主：Markdown 序列化器的构建器。 */
const MARKDOWN_SERIALIZER_BUILDER = 'ext:fof/rich-text/common/tiptap/markdown/MarkdownSerializerBuilder';

/**
 * 腿 A —— 注册自研 Tiptap 节点。
 *
 * buildExtensions() 返回的是一个全新的 ItemList（每次编辑器构建都会重建），
 * 所以在这里原地 add 不会重复、也不影响内置扩展。
 *
 * 关于排序：ItemList.add(key, content, priority = 0)，toArray() 按 priority **降序**排序，
 * 而 V8 的 Array.prototype.sort 是稳定的 ⇒ 同为 0 优先级时保持 add 顺序。
 * 内置的 'image' 也是 0，因此本节点只需在它之后 add，就能稳定排在 image 之后。
 */
function registerNode() {
  extend(TIPTAP_EDITOR_DRIVER, 'buildExtensions', function (items) {
    if (!items || typeof items.add !== 'function') return;
    const node = createUplImagePreviewNode();
    if (!node) return; // 取不到 Tiptap primitives 时降级跳过（getTiptapPrimitives 已打日志）

    items.add(UPL_IMAGE_PREVIEW_NODE, node);
  });
}

/**
 * 腿 B —— 让解析链路认得出 [upl-image-preview …]。
 *
 * 分词：插在 markdown-it 的 'link' 规则之前。插在 link 之前有两个好处：
 *   · 早于任何可能把 `[` 当作链接起点的规则，保证我们的标签被优先识别；
 *   · 又晚于 text 规则（text 会在遇到 '[' 这个终止符时停下），位置刚刚好。
 * 映射：把 token 类型登记成「node 型」，getAttrs 负责把 token.content 还原成节点属性。
 *   getAttrs 的签名是 (token, tokens, index)，本项目只需要第一个参数。
 */
function registerParser() {
  extend(MARKDOWN_PARSER_BUILDER, 'buildTokenizer', function (markdownIt) {
    if (!markdownIt || !markdownIt.inline || !markdownIt.inline.ruler) return;
    markdownIt.inline.ruler.before('link', UPL_IMAGE_PREVIEW_TOKEN, tokenizeUplImagePreview);
  });
  extend(MARKDOWN_PARSER_BUILDER, 'buildTokens', function (tokens) {
    if (!tokens) return;
    tokens[UPL_IMAGE_PREVIEW_TOKEN] = {
      node: UPL_IMAGE_PREVIEW_NODE,
      getAttrs: token => toNodeAttrs(parseUplImagePreviewAttributes(token?.content ?? ''))
    };
  });
}

/**
 * 腿 C —— 把节点原样还原成 fof/upload 认得的 BBCode。
 *
 * 这里用 state.write() 而不是 state.text()：
 *   fof/rich-text 重写过 MarkdownSerializerState.prototype.esc（会转义 ` * \ ~ 等字符），
 *   而 state.write() **不做转义**，正是「原样还原 BBCode」所需要的 —— 保证落库内容
 *   与改造前逐字节一致，content-guard 的第 ⑤ 条（upl-image-preview 的 url/thumbnail_url）
 *   才能继续命中（§8.6 红线）。
 */
function registerSerializer() {
  extend(MARKDOWN_SERIALIZER_BUILDER, 'buildNodes', function (nodes) {
    if (!nodes) return;
    nodes[UPL_IMAGE_PREVIEW_NODE] = (state, node) => {
      state.write(formatUplImagePreview(node.attrs));
    };
  });
}

/** 一次性注册三腿。由 src/forum/index.ts 在 jiedh-upload 初始化器里调用。 */
export function registerUplImagePreview() {
  registerNode();
  registerParser();
  registerSerializer();
}